import { useState } from 'react'
import { useMyGames, useUpdateGameInfo } from '@/hooks/queries'
import { backgroundImage } from '@/lib/format'
import { cn } from '@/lib/cn'
import { GameInfo } from '@/models/game-info'
import { Media } from '@/models/media'
import { CustomGameStep } from '@/components/giveaway-new/CustomGameStep'
import { SkeletonLine } from '@/components/ui/Skeleton'
import { AccountPage } from './account/AccountPage'
import { GameWebsiteLink } from '@/components/GameWebsiteLink'

/** The API returns the cover as an id; the form previews a Media. */
function withCoverMedia(game: GameInfo): GameInfo {
  if (game.cover_media || !game.cover_media_id) return game
  const copy = new GameInfo(game)
  copy.cover_media = new Media({ id: game.cover_media_id, src: game.display_cover_media })
  return copy
}

function GameCard({ game, onEdit }: { game: GameInfo; onEdit: () => void }) {
  const art = game.display_cover_media
  return (
    <article className="key-card game-card">
      <div
        className={cn('key-card-art game-card-art', !art && 'placeholder')}
        style={art ? backgroundImage(art) : undefined}
        aria-hidden="true"
      />
      <div className="key-card-body">
        <div className="key-card-heading">
          <h3 className="key-card-title" title={game.name ?? ''}>
            {game.name}
          </h3>
          {game.description ? <p className="game-card-description">{game.description}</p> : null}
          <GameWebsiteLink game={game} />
        </div>
        {game.tags.length > 0 ? (
          <div className="game-card-tags">
            {game.tags.map((tag) => (
              <span className="game-card-tag" key={tag}>
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        <button type="button" className="game-card-edit" onClick={onEdit}>
          Edit game
        </button>
      </div>
    </article>
  )
}

function GameCardSkeleton() {
  return (
    <article className="key-card game-card is-skeleton" aria-hidden="true">
      <div className="key-card-art game-card-art skeleton" />
      <div className="key-card-body">
        <div className="key-card-title">
          <SkeletonLine width="60%" />
        </div>
        <SkeletonLine width="85%" />
      </div>
    </article>
  )
}

/** `/my/games` — custom games this account added while creating giveaways. */
export function MyGamesPage() {
  const { data: games = [], isPending } = useMyGames()
  const update = useUpdateGameInfo()
  const [editingId, setEditingId] = useState<number | null>(null)

  const editing = games.find((game) => game.id === editingId) ?? null

  return (
    <AccountPage
      title="My Games"
      meta={
        isPending ? (
          <SkeletonLine width="5em" />
        ) : (
          `${games.length} custom ${games.length === 1 ? 'game' : 'games'}`
        )
      }
    >
      {editing ? (
        <div className="game-edit">
          <CustomGameStep
            key={editing.id}
            initial={withCoverMedia(editing)}
            title={`Edit ${editing.name ?? 'game'}`}
            confirmLabel={update.isPending ? 'Saving…' : 'Save'}
            cancelLabel="Cancel"
            footnote={null}
            busy={update.isPending}
            onCancel={() => setEditingId(null)}
            onConfirm={(game) => {
              if (editing.id === null) return
              update.mutate({ id: editing.id, game }, { onSuccess: () => setEditingId(null) })
            }}
          />
        </div>
      ) : isPending ? (
        <div className="loot-grid">
          {Array.from({ length: 3 }, (_, index) => (
            <GameCardSkeleton key={index} />
          ))}
        </div>
      ) : games.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-title">No custom games yet</div>
          <div className="empty-state-body">
            When a game isn&rsquo;t in our search, add it while creating a giveaway and it shows up
            here for you to edit.
          </div>
        </div>
      ) : (
        <div className="loot-grid">
          {games.map((game) => (
            <GameCard key={game.id} game={game} onEdit={() => setEditingId(game.id)} />
          ))}
        </div>
      )}
    </AccountPage>
  )
}
